import { useState, useCallback } from 'react';
import { Typography, Space, Segmented, Tag, Button, Tooltip, theme } from 'antd';
import { UserOutlined, SettingOutlined, CloseOutlined, LogoutOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSuperAdmins } from '../../hooks/use_super_admins';
import { useGatheringIdentity } from '../../hooks/use_gathering_identity';
import MemberView from './MemberView';
import AdminView from './AdminView';
import PieceBackdrop from '../Skargard/PieceBackdrop';
import Fold from '../Skargard/Fold';
import './Gathering.css';

const { Paragraph } = Typography;

// antd preset colours, read from the theme so they follow light/dark. A
// group's `color` (stored on the backend) indexes into this list —
// pods/gatherings/service.py PALETTE_SIZE must match its length.
const PALETTE_KEYS = ['blue', 'magenta', 'green', 'orange', 'purple', 'cyan', 'gold', 'volcano', 'geekblue', 'lime'];

/**
 * Gathering — node-level breakout rooms.
 *
 * Everyone lands in the member view, branded as the node like the guest join
 * page: pick the groups that fit you, then see the room you've been put in.
 * From the waiting room on, the member view sits on the same moving backdrop
 * as the join page and the live flow. Node superadmins land there too, with a toggle
 * to an admin view: the groups and who picked them, the ideal
 * room, and the planned rooms to reshuffle, adjust and publish.
 *
 * A guest (joined at /gathering/join, no account) gets the member view only,
 * and a "Leave" instead of the close button, since the rest of the app is off
 * limits to them. That holds even when the same browser is also signed in to
 * Keycloak as a superadmin: while the page acts as the guest, it is the guest.
 *
 * Successor to the context-scoped breakout composer (Hangouts/
 * BreakoutPanel.jsx). Backend: pods/gatherings. Planner: utils/gathering.js.
 */
function GatheringPage() {
    const { token } = theme.useToken();
    const navigate = useNavigate();
    const { isServerAdmin } = useSuperAdmins();
    const { isGuest, name: guestName, leave } = useGatheringIdentity();
    // useSuperAdmins asks about the Keycloak user, which a guest session can
    // sit on top of in the same browser — the guest mustn't inherit it.
    const isAdmin = isServerAdmin && !isGuest;
    // Admins land in the member view too; the toggle switches for this visit only.
    const [view, setView] = useState('member');
    const showAdmin = isAdmin && view === 'admin';
    // Where the gathering is, as MemberView last loaded it.
    const [memberStatus, setMemberStatus] = useState(null);
    const backdrop = !showAdmin && (memberStatus === 'waiting' || memberStatus === 'open');

    const paletteColor = useCallback(i => token[PALETTE_KEYS[(i ?? 0) % PALETTE_KEYS.length]], [token]);

    return (
        // Always the backdrop element, piece or not, so it coming in doesn't
        // remount (and reload) the view inside.
        <PieceBackdrop piece={Fold} followTheme showPiece={backdrop}>
            <div className="gathering-page">
                {/* Pinned to the viewport corner, same spot as the entity
                    page's Close (Entity.css .entity-corner-actions) — the
                    two full-screen pages read as one consistent chrome.
                    No NavBar on this page (see RouteMap.js) — this is the
                    only way back. */}
                <div className="gathering-corner-actions">
                    {isGuest ? (
                        <Tooltip title="Leave the Gathering — you can join again with the passcode">
                            <Button
                                type="text"
                                className="gathering-icon-btn"
                                icon={<LogoutOutlined />}
                                onClick={() => { leave(); navigate('/gathering/join', { replace: true }); }}
                            >
                                Leave
                            </Button>
                        </Tooltip>
                    ) : (
                        <Tooltip title="Close">
                            <Button
                                type="text"
                                className="gathering-icon-btn gathering-icon-btn--close"
                                icon={<CloseOutlined />}
                                onClick={() => navigate('/')}
                            />
                        </Tooltip>
                    )}
                </div>
                {(isGuest || isAdmin) && (
                    <div className="gathering-header">
                        {isGuest && <Tag color="gold">Guest · {guestName}</Tag>}
                        {isAdmin && (
                            <Space align="center">
                                <Segmented
                                    value={showAdmin ? 'admin' : 'member'}
                                    onChange={setView}
                                    options={[
                                        { label: 'Member view', value: 'member', icon: <UserOutlined /> },
                                        { label: 'Admin view', value: 'admin', icon: <SettingOutlined /> },
                                    ]}
                                />
                            </Space>
                        )}
                    </div>
                )}
                {/* The member intro lives in MemberView — it shouldn't greet
                    someone who's still in the waiting room. */}
                {showAdmin && (
                    <Paragraph type="secondary" className="gathering-lede">
                        People mark themselves into groups. Describe the ideal room, check the plan, adjust it by hand,
                        then publish so everyone sees their room.
                    </Paragraph>
                )}

                {showAdmin
                    ? <AdminView paletteColor={paletteColor} />
                    : <MemberView colorOf={g => paletteColor(g.color)} onStatus={setMemberStatus} />}
            </div>
        </PieceBackdrop>
    );
}

export default GatheringPage;
